import React, { Fragment, useState, useEffect } from 'react';
import { useTable, useFilters, useSortBy, usePagination, useRowSelect, useExpanded } from 'react-table';
import { Table, Container, Row, Col, Button, Input, CustomInput, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Filter, DefaultColumnFilter } from './filters';
import { useExportData } from "react-table-plugins";
import Papa from "papaparse";
import XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable'
import { useSticky } from "react-table-sticky";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDoubleRight, faAngleDoubleLeft, faAngleRight, faAngleLeft, faSlidersH, faFileCsv, faFileExcel, faFilePdf, faSort, faFilter, faFileDownload } from '@fortawesome/free-solid-svg-icons'
import { getEncStatut, getCommissionType, getEncModeRegl, getCommissionWarning } from "../../services/misc";
import ReactTooltip from 'react-tooltip';

const SELECTION_ID = "selection"

interface Props {
  indeterminate?: boolean;
  name: string;
}

const useCombinedRefs = (...refs): React.MutableRefObject<any> => {
  const targetRef = React.useRef();

  React.useEffect(() => {
    refs.forEach(ref => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};

const IndeterminateCheckbox = React.forwardRef<HTMLInputElement, Props>(
  ({ indeterminate, ...rest }, ref: React.Ref<HTMLInputElement>) => {
    const defaultRef = React.useRef(null)
    // const resolvedRef = ref || defaultRef
    const resolvedRef = useCombinedRefs(ref, defaultRef);

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    return (
      <>
        <input type="checkbox" ref={resolvedRef} {...rest} />
      </>
    )
  }
)

const TableContainer = ({ columns, data, setHmm = (...e) => {}, showPagination = true, dataName = "", showFooter = false, enableCheckBox, checkBoxChecksAllPages = false, selectTrigger = 0, setFilteredRows = (...e) => {}, setSelectedRows = (...e) => {}, setSelectedRowsIds = (...e) => {}, isEditable = false, updateMyData = (...e) => {}, skipPageReset = false, disableFilter = false, hideFiltersByDefault = false, clearFilters = false, setClearFilters = (e: boolean) => {}, clearRowsKey = 0, noDataText = "Aucune donnée disponible", hideExport = false, hideSettings = false, centerCols = false, smallerCols = false, hideFirstCol = false, bgColor = "", ignoreDangerCheckbox = false, SubComponent = (...e) => <></> }) => {
  const filterTypes = React.useMemo(
    () => ({
        dateBetween: (rows, id, filterValues) => {
          let sd = filterValues[0] ? new Date(filterValues[0]) : undefined;
          let ed = filterValues[1] ? new Date(filterValues[1]) : undefined;
          
          if(sd || ed)
            return rows.filter(r => {
                var time = new Date(r.values[id]);
                if (filterValues.length === 0) return rows;
                return ((!sd || time >= sd) && (!ed || time <= ed));
            });
          else
            return rows
        }
    }),
    []
  );

  const randomKey = Math.random().toString(36).substr(2, 5)
  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    page,
    prepareRow,
    allColumns,
    getToggleHideAllColumnsProps,
    visibleColumns,
    filteredRows,

    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize, selectedRowIds, expanded },
    exportData,
    setAllFilters,
  } = useTable({
    columns,
    data,
    // defaultColumn: { Filter: DefaultColumnFilter },
    defaultColumn: React.useMemo(
      () => ({
        Filter: DefaultColumnFilter,
      }),
      []
    ),
    filterTypes,
    initialState: { 
      initialState: {
        hiddenColumns: ["id"]
      },
      pageIndex: 0,
      // ...(showPagination ? {pageSize: 10} : {}),
      ...(showPagination ? {pageSize: 10} : {pageSize: 99999}),
      hiddenColumns: columns.filter((e) => e.hidden).map(e => e.id || (typeof e.accessor === "string" ? e.accessor : e.Header))
    },
    autoResetPage: false,//!skipPageReset,
    autoResetSelectedRows: false,
    
    autoResetExpanded: false,
    autoResetGroupBy: false,
    autoResetSortBy: false,
    autoResetFilters: false,
    autoResetRowState: false,

    // disableMultiSort: false,

    getExportFileBlob,
    getExportFileName,
    ...(isEditable && {updateMyData}),
    useExpanded
  },
  useFilters,
  useSortBy,
  usePagination,
  useRowSelect,
  // useSticky,
  useExportData,
  hooks => {
    enableCheckBox && hooks.visibleColumns.push(columns => [
      // Let's make a column for selection
      {
        id: SELECTION_ID,
        accessor: SELECTION_ID,
        disableFilters: true,
        // disableSortBy: true,
        // The header can use the table's getToggleAllRowsSelectedProps method
        // to render a checkbox
        Header: ({ getToggleAllPageRowsSelectedProps, getToggleAllRowsSelectedProps, ...rest }) => (
          <div>
            {!!(rest.rows.every(e => !e.original.isDangerRow) || ignoreDangerCheckbox) && <IndeterminateCheckbox {...(checkBoxChecksAllPages ? getToggleAllRowsSelectedProps() : getToggleAllPageRowsSelectedProps())} title={checkBoxChecksAllPages ? "Cocher/Décocher toutes les lignes de toutes les pages" : "Cocher/Décocher toutes les lignes de la page"} />}
            <FontAwesomeIcon icon={faSort} />
          </div>
        ),
        sortType: (a, b, id, desc) => a.isSelected && !b.isSelected ? -1 : !a.isSelected && b.isSelected ? 1 : 0,
        // The cell can use the individual row's getToggleRowSelectedProps method
        // to the render a checkbox
        Cell: ({ row }) => (
          <div>
            {!!(!row.original.isDangerRow || ignoreDangerCheckbox) && <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} className="tableCheck" title="" />}
          </div>
        ),
      },
      ...columns,
    ])
  });

  const [showFiltersBtn, setShowFiltersBtn] = useState(!disableFilter)
  const [filtersVisibility, setFiltersVisibility] = useState(!disableFilter && !hideFiltersByDefault)
  let onlyExportFilteredRows = false
  const [disableExport, setDisableExport] = useState(false)

  useEffect(() => {
    setShowFiltersBtn(!disableFilter)
    setFiltersVisibility(!disableFilter && !hideFiltersByDefault)
  }, [disableFilter, hideFiltersByDefault])

  useEffect(() => {
    if(setFilteredRows)
      setFilteredRows(filteredRows?.map(e => e.original) || [])
  }, [filteredRows])
  
  const possiblePageCounts = [10, 20, 30, 40, 50, 100, 1000];
  const [modal, setModal] = useState(false);
  
  const handleToggle = () => setModal(!modal);

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : '';
    // return column.isSorted ? (column.isSortedDesc ? ' 🢃' : ' 🢁') : '';
    // return column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : '';
  };

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };

  const onChangeInInput = (event) => {
    const page = event.target.value ? (Number(event.target.value) == 0 ? 0 : Number(event.target.value) - 1) : 0;
    gotoPage(page);
  };

  // useEffect(() => {
  //   if(page.length){
  //   // console.log(page[0].cells[6])
  //   // page[0].cells[6].value=777
  //   // page[0].toggleRowSelected()
  //   console.log("p", page[0].cells[6].getCellProps())
  //   }
  // }, [page])

  useEffect(() => {
    if(clearFilters){
      setAllFilters([]);
      setClearFilters(false);
    }
  }, [clearFilters])

  useEffect(() => {
    toggleAllRowsSelected(false);
  }, [clearRowsKey])

  useEffect(() => {
    if(selectTrigger)
      page.forEach(e => {
        e.toggleRowSelected(!e.original.isDangerRow)
      })
  }, [selectTrigger])

  useEffect(() => {
    // console.log("HERE", selectedFlatRows)
    // if(enableCheckBox)
      setSelectedRows(selectedFlatRows.map(e => e.original))
  }, [selectedFlatRows?.length])

  useEffect(() => {
    if(enableCheckBox)
      setSelectedRowsIds(selectedRowIds)
  }, [selectedRowIds?.length])

  function getExportFileName({ fileType, all }) {
    return "export_table" + (dataName ? "_" + dataName : "") + "_" + Math.random().toString(36).substr(2, 5)
  }

  function getExportFileBlob({ columns, fileType, fileName, data: paramData }) {
    setDisableExport(true)
    setTimeout(() => setDisableExport(false), 3000)
    let dataCloned = data,
      displayData: any[] = [],
      visibleColsData: any[] = [];
    const header = columns.filter(e => e.id !== SELECTION_ID).map(({exportValue}, index, arr) => `${exportValue}${arr.filter(e => e.exportValue === exportValue).length > 1 ? ` (${Number(arr.map((e, i) => [e, i]).filter(e => e[0].exportValue === exportValue).findIndex(e => e[1] === index)) + 1})` : ""}`);
    const visibleColsHeader = columns.filter(e => e.id !== SELECTION_ID).filter(e => !!e.isVisible).map(({exportValue}, index, arr) => `${exportValue}${arr.filter(e => e.exportValue === exportValue).length > 1 ? ` (${Number(arr.map((e, i) => [e, i]).filter(e => e[0].exportValue === exportValue).findIndex(e => e[1] === index)) + 1})` : ""}`);
    let indexes = Array.from(Array(paramData.length).keys())
    if(selectedFlatRows.length)
      indexes = indexes.filter(t => selectedFlatRows.find(e => e.index === t))
    if(onlyExportFilteredRows)
      indexes = indexes.filter(t => filteredRows.find(e => e.index === t))
    const getEncStatutHandle = e => getEncStatut(e)
    const getCommissionTypeHandle = e => getCommissionType(e)
    const getEncModeReglHandle = e => getEncModeRegl(e, false)
    const getCommissionWarningHandle = e => getCommissionWarning(e)
    for(let i of indexes){
      let line: any[] = [];
      let visibleColsline: any[] = [];
      for(let j in columns){
        if(columns[j].id !== SELECTION_ID){
          if(columns[j].customExportDisplay){
            eval(`line.push(${columns[j].customExportDisplay.replace(/\$/g, "dataCloned[i]")})`)
            if(columns[j].isVisible)
              eval(`visibleColsline.push(${columns[j].customExportDisplay.replace(/\$/g, "dataCloned[i]")})`)
          }
          else{
            line.push(paramData[i][j])
            // line.push(dataCloned[i][columns[j].id])
            if(columns[j].isVisible)
              visibleColsline.push(paramData[i][j])
          }
        }
      }
      displayData.push(line);
      visibleColsData.push(visibleColsline);
    }
    
    if (fileType === "csv") {
      const csvString = Papa.unparse({ fields: header, data: displayData });
      return new Blob(["\ufeff" + csvString], { type: "text/csv; charset=utf-18" });
    }
    else if (fileType === "xlsx") {
      const compatibleData = displayData.map((row) => {
        const obj = {};
        header.forEach((col, index, arr) => {
          obj[`${col}${arr.filter(e => e === col).length > 1 ? ` (${Number(arr.map((e, i) => [e, i]).filter(e => e[0] === col).findIndex(e => e[1] === index)) + 1})` : ""}`] = row[index];
          obj[col] = row[index];
        });
        return obj;
      });

      const jsonKeys = header ? header : Object.keys(compatibleData[0]);
      let objectMaxLength: any[] = []; 
      for (let i = 0; i < compatibleData.length; i++) {
        let value: any = Object.values(compatibleData[i]);
        for (let j = 0; j < value.length; j++) {
          if (typeof value[j] == "number") {
            objectMaxLength[j] = 10;
          } else if(value[j] !== undefined && value[j] !== null){
            objectMaxLength[j] =
              objectMaxLength[j] >= value[j].length
                ? objectMaxLength[j]
                : value[j].length;
          }
        }
        let key = jsonKeys;
        for (let j = 0; j < key.length; j++) {
          objectMaxLength[j] =
            objectMaxLength[j] >= key[j].length
              ? objectMaxLength[j]
              : key[j].length;
        }
      }

      const wscols = objectMaxLength.map(w => { return { width: w + 2 } });

      const ws1: XLSX.WorkSheet = XLSX.utils.json_to_sheet(compatibleData, {
        header,
      });
      ws1["!cols"] = wscols;
  
      let wb = XLSX.utils.book_new();
      // let ws1 = XLSX.utils.json_to_sheet(compatibleData, {
      //   header,
      // });
      XLSX.utils.book_append_sheet(wb, ws1, "table");
      XLSX.writeFile(wb, `${fileName}.xlsx`);
  
      return false;
    }
    if (fileType === "pdf") {
      let textSize = Math.round(100 / visibleColsHeader.length);
      if(textSize > 12)
        textSize = 12
      else if(textSize < 4)
        textSize = 4
      const doc = new jsPDF({orientation: "landscape"});
      autoTable(doc, {
        head: [visibleColsHeader],
        body: visibleColsData,
        margin: { top: 20 },
        // styles: { overflow: 'ellipsize', cellWidth: 'wrap' },
        styles: {
          minCellHeight: 8,
          halign: "left",
          valign: "middle",
          // valign: "center",
          fontSize: textSize//11
        },
      });
      doc.save(`${fileName}.pdf`);
  
      return false;
    }
    
    return false;
  }

  const ExportTooltip = ({id, type}) => <ReactTooltip effect="solid" globalEventOff='click' isCapture clickable id={id} type="dark">
    <div className="tableExportOptionsBtns" style={{color: "lime", ...(filteredRows?.length === data?.length ? {opacity: .3, cursor: "default"} : {})}} onClick={() => {
      if(!disableExport && filteredRows?.length !== data?.length){
        onlyExportFilteredRows = true
        exportData(type, true)
      }
    }}><FontAwesomeIcon icon={faFileDownload} />&nbsp;Lignes filtrées{filteredRows?.length === data?.length ? "" : ` (${filteredRows?.length || 0})`}</div>
    <div className="tableExportOptionsBtns" style={{color: "white"}} onClick={() => {
      if(!disableExport){
        onlyExportFilteredRows = false
        exportData(type, true)
      }
    }}><FontAwesomeIcon icon={faFileDownload} />&nbsp;Toutes les lignes ({data?.length || 0})</div>
  </ReactTooltip>

  return (
    <Fragment>
      <div style={{position: "relative"}}>
        <div className="tableFloatingBtns">
          {!!showFiltersBtn && <div onClick={() => {setFiltersVisibility(e => !e); setAllFilters([]);}} title="Afficher/masquer les filtres"><FontAwesomeIcon icon={faFilter} /></div>}
          {!hideSettings && <div onClick={handleToggle} title="Afficher/masquer les colonnes"><FontAwesomeIcon icon={faSlidersH} /></div>}
        </div>
        <Table bordered hover striped responsive {...(hideFirstCol || centerCols || smallerCols ? {className: `${hideFirstCol ? "hideFirstCol" : ""} ${centerCols ? "centerCols" : ""} ${smallerCols ? "smallerCols" : ""}`} : {})} {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, key) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={key}>
                {headerGroup.headers.map((column, key2) => (
                  <th {...column.getHeaderProps()} style={{position: "sticky", top: "0", ...(bgColor && (!hideFirstCol || key2 !== 0) ? {backgroundColor: bgColor} : {})}} key={key2}>
                    <div {...column.getSortByToggleProps({ title: column.canSort ? `Trier${column?.id !== SELECTION_ID ? ` ${(column?.Header || "")}` : ""}` : undefined })} style={{...column.getSortByToggleProps().style, ...(column.filter === "dateBetween" ? {whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"} : {})}}>
                      {column.render('Header')}
                      {generateSortingIndicator(column)}
                    </div>
                    {!!filtersVisibility && <Filter column={column} />}
                    {/* {!disableFilter && <Filter column={column} />} */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length ? page.map((row, key) => {
              prepareRow(row);
              return (<Fragment key={key}>
                <tr {...row.getRowProps()} className={selectedRowIds[row.id] ? "tableRowSelected": row.original.isDangerRow ? "tableRowDanger" : ""} style={row.original.customStyle ? row.original.customStyle : {}}>
                  {row.cells.map((cell, key2) => {
                    return <td {...cell.getCellProps({
                      className: cell.column.className,
                      
                    })} style={{
                      ...(bgColor && cell.column.className?.split(" ").includes("tableLeftHeader") ? {backgroundColor: bgColor} : {}),
                      // ...(bgColor && cell.column.className?.split(" ").includes("tableCellRightBorder") ? {borderRightColor: bgColor + " !important"} : {}),
                    }} ref={(node) => {
                      if(node && bgColor && cell.column.className?.split(" ").includes("tableCellRightBorder")) node.style.setProperty("border-right-color", bgColor, "important")
                    }} key={key2}>{cell.render('Cell')}</td>;
                  })}
                </tr>
                {!row.subRows.length && row.isExpanded ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      {SubComponent({ row })}
                    </td>
                  </tr>
                ) : null}</Fragment>
              );
            }) : (<tr><td colSpan={42} className="noTableData">{noDataText}</td></tr>)}
          </tbody>
          {!!showFooter && <tfoot>
            {footerGroups.map(group => (
              <tr {...group.getFooterGroupProps()}>
                {group.headers.map(column => (
                  <td {...column.getFooterProps()} className={column.getFooterProps().className + (column.render('Footer')?.type?.name === "Footer" ? (" tableFooterColumnWithContent") : "")}>{column.render('Footer')}</td>
                ))}
              </tr>
            ))}
          </tfoot>}
        </Table>
      </div>

      <Modal isOpen={modal} toggle={handleToggle} className="tableSettingsModal">
        <ModalHeader toggle={handleToggle}><FontAwesomeIcon icon={faSlidersH} />&nbsp;&nbsp;Afficher/masquer les colonnes</ModalHeader>
        <ModalBody>
          <Container>
            <Row>
              <Col xs="12">
                <label><IndeterminateCheckbox {...getToggleHideAllColumnsProps()} /> Cocher/décocher tout</label>
              </Col>
              {allColumns.filter(column => !!column.Header).map(column => (
                <Col xs="12" sm="6" key={column.id}>
                  <label>
                    <input type="checkbox" {...column.getToggleHiddenProps()} />{' '}
                    {column.Header}
                  </label>
                </Col>
              ))}
            </Row>
          </Container>
        </ModalBody>
      </Modal>

      {showPagination && data.length > Math.min(...possiblePageCounts) ? (
        <Row className="paginationRow">
          <Col xs={{size: 4, order: 1}} md={{order: 1, size: 3}} style={{ alignSelf: "center" }} className="navigationButtonsCol">
            <Button
              color="dark"
              size="sm"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </Button>
            <span style={{flex: 0.1}}>{' '}</span>
            <Button
              color="dark"
              size="sm"
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </Button>
          </Col>
          <Col xs={{size: 4, order: 1}} md={{order: 1, size: 2}} style={{ alignSelf: "center" }}>
            Page{' '}
            <strong>
              {pageIndex + 1}/{pageOptions.length == 0 ? 1 : pageOptions.length}
            </strong>
          </Col>
          <Col xs={{size: 6, order: 2}} md={{order: 1, size: 2}} style={{ alignSelf: "center" }}>
            <Input
              type='number'
              className="paginationPageInput"
              min={1}
              style={{ width: 70 }}
              max={pageOptions.length === 0 ? 1 : pageOptions.length}
              defaultValue={pageIndex + 1}
              pattern="[0-9]*"
              onChange={onChangeInInput}
            />
          </Col>
          <Col xs={{size: 6, order: 2}} md={{order: 1, size: 2}} style={{ alignSelf: "center" }}>
            <CustomInput
              id="pageSizeSelect"
              type='select'
              value={pageSize}
              onChange={onChangeInSelect}
              >
              {possiblePageCounts.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Afficher {pageSize}
                </option>
              ))}
            </CustomInput>
          </Col>
          <Col xs={{size: 4, order: 1}} md={{order: 1, size: 3}} style={{ alignSelf: "center" }} className="navigationButtonsCol">
            <Button color="dark" size="sm" onClick={nextPage} disabled={!canNextPage}>
              <FontAwesomeIcon icon={faAngleRight} />
            </Button>
            <span style={{flex: 0.1}}>{' '}</span>
            <Button
              color="dark"
              size="sm"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            </Button>
          </Col>
        </Row>
      ) : null}
      {!hideExport && data.length && data.length > 0 ? (<div className="tableExportDiv">
          <span style={{display: "inline-block"}}>Exporter {selectedFlatRows.length ? (selectedFlatRows.length === 1 ? "la ligne sélectionnée" : `les (${selectedFlatRows.length}) lignes sélectionnées`) : "la table"} en:</span>
          <span style={{display: "flex"}}>
            {/* <button className="tableExportBtn"
              onClick={() => {
                exportData("csv", true);
              }}
            ><FontAwesomeIcon icon={faFileCsv} style={{color: "darkseagreen"}} />&nbsp;<div className="tableExportBtnText">CSV</div></button>
            <button className="tableExportBtn"
              onClick={() => {
                exportData("xlsx", true);
              }}
              ><FontAwesomeIcon icon={faFileExcel} style={{color: "darkgreen"}} />&nbsp;<div className="tableExportBtnText">Excel</div></button>
            <button className="tableExportBtn"
              onClick={() => {
                exportData("pdf", true);
              }}
              ><FontAwesomeIcon icon={faFilePdf} style={{color: "darkred"}} />&nbsp;<div className="tableExportBtnText">PDF</div></button> */}
            <button className="tableExportBtn" data-tip data-for={`exportCSV_TT${randomKey}`} data-event='click'><FontAwesomeIcon icon={faFileCsv} style={{color: "darkseagreen"}} />&nbsp;<div className="tableExportBtnText">CSV</div></button>
            <ExportTooltip id={`exportCSV_TT${randomKey}`} type="csv" />
            <button className="tableExportBtn" data-tip data-for={`exportXLSX_TT${randomKey}`} data-event='click'><FontAwesomeIcon icon={faFileExcel} style={{color: "darkgreen"}} />&nbsp;<div className="tableExportBtnText">Excel</div></button>
            <ExportTooltip id={`exportXLSX_TT${randomKey}`} type="xlsx" />
            <button className="tableExportBtn" data-tip data-for={`exportPDF_TT${randomKey}`} data-event='click'><FontAwesomeIcon icon={faFilePdf} style={{color: "darkred"}} />&nbsp;<div className="tableExportBtnText">PDF</div></button>
            <ExportTooltip id={`exportPDF_TT${randomKey}`} type="pdf" />
          </span>
      </div>) : null}
    </Fragment>
  );
};

export default TableContainer;
